import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/** Per-plan leading+trailing coalescing state for `PlanRefreshService.refreshPlan()`. See Issue #1603. */
interface CoalesceState {
  cooldownTimer: ReturnType<typeof setTimeout>;
  pendingReason: string | null;
}

/**
 * Service for triggering plan data refresh across components.
 * Used when plan pages are added/modified and the UI needs to refresh.
 */
@Injectable({
  providedIn: 'root',
})
export class PlanRefreshService {
  private planRefreshSubject = new Subject<{ planId: string; reason: string }>();
  private complianceRefreshSubject = new Subject<{ planId: string; pageNumber: number; reason: string }>();

  // Observable for components to listen to plan refresh events
  planRefresh$ = this.planRefreshSubject.asObservable();

  // Observable for compliance component to listen to compliance data refresh events
  complianceRefresh$ = this.complianceRefreshSubject.asObservable();

  // Issue #1603: during a large parallel fan-out, dozens/hundreds of tasks for the same plan
  // can complete within the same second, each independently calling refreshPlan(). Emitting a
  // full plan reload (clearPlanDataCache + re-fetch getArchitecturalPlan) per call turns into a
  // refresh storm that makes the UI unusable and eventually starves the browser's connection
  // pool (surfacing as an "Error Loading Plan" / http status code 0 dialog). Coalesce bursts per
  // planId into at most one emission per COALESCE_WINDOW_MS, using leading+trailing semantics so
  // a single completion still refreshes immediately, while a sustained burst is capped to a
  // steady trickle instead of firing on every individual completion.
  private static readonly COALESCE_WINDOW_MS = 750;
  private coalesceStateByPlanId = new Map<string, CoalesceState>();

  constructor() {}

  /**
   * Notify that plan data should be refreshed (e.g., after page ingestion)
   */
  refreshPlan(planId: string, reason: string = 'Plan data updated'): void {
    const state = this.coalesceStateByPlanId.get(planId);
    if (!state) {
      // Leading edge: no refresh in flight for this plan, emit immediately.
      this.emitPlanRefresh(planId, reason);
      this.coalesceStateByPlanId.set(planId, {
        cooldownTimer: setTimeout(() => this.flushCoalescedRefresh(planId), PlanRefreshService.COALESCE_WINDOW_MS),
        pendingReason: null,
      });
      return;
    }

    // A refresh for this plan already fired within the coalescing window — record the latest
    // reason and let the trailing flush emit it once the window closes, instead of emitting now.
    ClientLoggingService.log(
      'INFO',
      `[PlanRefreshService] Coalescing plan refresh for ${planId} within ${PlanRefreshService.COALESCE_WINDOW_MS}ms window: ${reason}`
    );
    state.pendingReason = reason;
  }

  /**
   * Trailing edge of the coalescing window for a plan: if any refreshPlan() calls came in while
   * the window was open, emit once more with the latest reason and re-arm the window so a
   * sustained burst keeps refreshing at a steady, bounded rate. Otherwise, the window simply
   * closes and the next refreshPlan() call becomes a fresh leading edge.
   */
  private flushCoalescedRefresh(planId: string): void {
    const state = this.coalesceStateByPlanId.get(planId);
    this.coalesceStateByPlanId.delete(planId);
    if (state?.pendingReason) {
      this.emitPlanRefresh(planId, state.pendingReason);
      this.coalesceStateByPlanId.set(planId, {
        cooldownTimer: setTimeout(() => this.flushCoalescedRefresh(planId), PlanRefreshService.COALESCE_WINDOW_MS),
        pendingReason: null,
      });
    }
  }

  private emitPlanRefresh(planId: string, reason: string): void {
    ClientLoggingService.log('INFO', `[PlanRefreshService] Triggering plan refresh for ${planId}: ${reason}`);
    this.planRefreshSubject.next({ planId, reason });
  }

  /**
   * Notify that plan pages have been added/updated
   */
  refreshPlanPages(planId: string, pageNumbers: number[]): void {
    const reason = `Pages ${pageNumbers.join(', ')} added/updated`;
    this.refreshPlan(planId, reason);
  }

  /**
   * Notify that compliance data should be refreshed for a specific page (e.g., after code applicability analysis)
   */
  refreshComplianceData(planId: string, pageNumber: number, reason: string = 'Compliance data updated'): void {
    ClientLoggingService.log(
      'INFO',
      `[PlanRefreshService] Triggering compliance refresh for ${planId} page ${pageNumber}: ${reason}`
    );
    this.complianceRefreshSubject.next({ planId, pageNumber, reason });
  }
}
